import { Button, cn } from '@buakei/ui';
import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

/*
 * Landing-page showcase of the two experiences.
 *
 * The previews are scaled-down renderings of the real Professional and Learning interfaces,
 * built from the same tokens and the same labels as the application. Where the real screens
 * show content, the previews use neutral placeholder lines: they deliberately contain no legal
 * cases, citations, holdings or AI answers, so nothing here can be mistaken for real authority.
 * Previews are non-interactive and exposed to assistive technology as a single labelled image.
 */

/** Neutral placeholder line standing in for real content. */
function Line({ className }: { className?: string }) {
  return <span className={cn('block h-1.5 rounded-full bg-line-200', className)} />;
}

function PreviewWindow({
  label,
  scope,
  children,
}: {
  label: string;
  scope: string;
  children: ReactNode;
}) {
  return (
    // Framed like a window that runs off the panel: cropped by the panel's bottom edge (and its
    // right edge from tablet width up), so the product reads as large and real.
    <div className="flex h-full flex-col overflow-hidden rounded-t-xl border border-b-0 border-line-200 bg-white text-left md:w-[112%] md:rounded-tr-none md:border-r-0">
      <div className="flex items-center gap-2 border-b border-line-200 px-3 py-2">
        <img src="/brand/mark-navy.svg" alt="" className="size-4" width={16} height={16} />
        <span className="text-xs font-semibold text-navy-900">{label}</span>
        <span className="ml-auto hidden text-[11px] text-muted-600 sm:inline">{scope}</span>
      </div>
      {children}
    </div>
  );
}

function MiniNav({ items, active }: { items: string[]; active: string }) {
  return (
    <div className="hidden w-28 shrink-0 flex-col gap-1 bg-navy-950 p-2 sm:flex">
      {items.map((item) => (
        <span
          key={item}
          className={cn(
            'rounded px-2 py-1 text-[11px]',
            item === active
              ? 'bg-navy-800 font-semibold text-white shadow-[inset_2px_0_0_var(--color-white)]'
              : 'text-navy-200',
          )}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

/** Professional research workbench: navigation, tools, composer and source inspector. */
function ProfessionalPreview() {
  return (
    <PreviewWindow label="Research workbench" scope="Personal scope">
      <div className="flex">
        <MiniNav items={['Home', 'Research', 'Drafting', 'Documents']} active="Research" />
        <div className="flex min-w-0 flex-1 flex-col gap-3 p-3">
          <div className="flex gap-4 border-b border-line-200 text-[11px]">
            <span className="-mb-px border-b-2 border-navy-900 pb-1.5 font-semibold text-navy-900">
              Ask Buakei
            </span>
            <span className="pb-1.5 text-muted-600">Search sources</span>
          </div>
          <div className="flex flex-wrap gap-1.5 text-[11px]">
            <span className="rounded border border-navy-900 px-2 py-0.5 font-medium text-navy-900 ring-1 ring-navy-900">
              Quick
            </span>
            <span className="rounded border border-line-200 px-2 py-0.5 text-muted-600">
              Comprehensive
            </span>
            <span className="rounded border border-line-200 px-2 py-0.5 text-muted-600">
              Custom plan
            </span>
          </div>
          <div className="flex flex-col gap-2 rounded-md border border-line-200 p-2.5">
            <span className="text-[11px] font-medium text-navy-900">
              Ask a legal research question
            </span>
            <Line className="w-11/12" />
            <Line className="w-2/3" />
            <span className="self-end rounded bg-navy-900 px-2.5 py-1 text-[11px] font-medium text-white">
              Ask
            </span>
          </div>
          <div className="flex flex-col gap-2 rounded-md border border-line-200 p-2.5">
            <span className="text-[11px] font-semibold text-navy-900">Source inspector</span>
            <span className="flex flex-col gap-1.5 border-l-2 border-navy-900 pl-2.5">
              <Line className="w-full" />
              <Line className="w-4/5" />
            </span>
            <span className="text-[11px] font-medium text-navy-900">Verification by dimension</span>
            {['Source authenticity', 'Text accuracy', 'Legal status', 'Legal analysis'].map(
              (dimension) => (
                <span key={dimension} className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-muted-600">{dimension}</span>
                  <Line className="w-14" />
                </span>
              ),
            )}
          </div>
        </div>
      </div>
    </PreviewWindow>
  );
}

/** Learning study space: topic progress beside a practice quiz. */
function LearningPreview() {
  return (
    <PreviewWindow label="Your study space" scope="Learning — private to you">
      <div className="grid gap-3 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <div className="flex flex-col gap-2.5">
          <span className="text-[11px] font-semibold text-navy-900">Continue studying</span>
          {[60, 20, 0].map((progress, i) => (
            <span key={i} className="flex flex-col gap-1.5 rounded-md border border-line-200 p-2.5">
              <Line className={i === 0 ? 'w-3/4' : i === 1 ? 'w-1/2' : 'w-2/3'} />
              <span className="flex items-center gap-2">
                <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-line-200">
                  <span
                    className="block h-full rounded-full bg-navy-900"
                    style={{ width: `${progress}%` }}
                  />
                </span>
                <span className="w-7 text-right text-[10px] text-muted-600">{progress}%</span>
              </span>
            </span>
          ))}
        </div>
        <div className="flex flex-col gap-2 rounded-md border border-line-200 p-2.5">
          <span className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold text-navy-900">Practice quiz</span>
            <span className="rounded border border-line-200 px-1.5 text-[10px] text-muted-600">
              Question 1 of 3
            </span>
          </span>
          <Line className="w-full" />
          <Line className="w-3/5" />
          {[false, true, false].map((selected, i) => (
            <span
              key={i}
              className={cn(
                'flex items-center gap-2 rounded border p-2',
                selected ? 'border-navy-900 ring-1 ring-navy-900' : 'border-line-200',
              )}
            >
              <span
                className={cn(
                  'size-2.5 shrink-0 rounded-full border',
                  selected ? 'border-navy-900 bg-navy-900' : 'border-navy-400',
                )}
              />
              <Line className={i === 1 ? 'w-4/5' : 'w-3/5'} />
            </span>
          ))}
          <span className="self-start rounded bg-navy-900 px-2.5 py-1 text-[11px] font-medium text-white">
            Check answer
          </span>
        </div>
        <div className="flex flex-col gap-2 rounded-md border border-line-200 p-2.5 sm:col-span-2">
          <span className="text-[11px] font-medium text-navy-900">Ask Buakei</span>
          <Line className="w-5/6" />
          <Line className="w-1/2" />
          <span className="self-end rounded bg-navy-900 px-2.5 py-1 text-[11px] font-medium text-white">
            Ask
          </span>
        </div>
      </div>
    </PreviewWindow>
  );
}

type Panel = {
  id: string;
  title: string;
  description: string;
  cta: string;
  to: string;
  previewLabel: string;
  preview: ReactNode;
  dark: boolean;
};

const PANELS: Panel[] = [
  {
    id: 'professional',
    title: 'For Legal Professionals',
    description:
      'Research authorities, draft documents and manage matters in one secure workspace.',
    cta: 'Explore Professional Workspace',
    to: '/professional',
    previewLabel:
      'Preview of the Professional research workbench: navigation, research tools, question composer and source inspector with verification by dimension.',
    preview: <ProfessionalPreview />,
    dark: true,
  },
  {
    id: 'learning',
    title: 'For Law Students',
    description:
      'Understand legal principles, practise exam questions and develop practical skills.',
    cta: 'Explore Learning Workspace',
    to: '/learning',
    previewLabel:
      'Preview of the Learning study space: topic progress, a practice quiz question with answer options and the tutor question box.',
    preview: <LearningPreview />,
    dark: false,
  },
];

/** "Two Experiences. One Legal Intelligence Platform." landing-page section. */
export function ExperienceShowcase() {
  return (
    <section aria-labelledby="experiences" className="border-y border-line-200 bg-white">
      <div className="mx-auto flex max-w-6xl flex-col gap-12 px-4 pt-10 pb-16 md:pt-12 md:pb-20">
        <header className="mx-auto flex max-w-4xl flex-col items-center gap-5 text-center">
          <h2
            id="experiences"
            className="font-display text-2xl leading-tight text-navy-900 md:text-3xl lg:text-4xl lg:whitespace-nowrap"
          >
            Two Experiences. One Legal Intelligence Platform.
          </h2>
          <p className="max-w-3xl text-lg text-muted-600">
            Purpose-built for the practice and study of Sierra Leonean law. Whether you&rsquo;re
            handling a matter or preparing for your next examination, Buakei AI brings the tools
            together.
          </p>
        </header>

        <div className="grid gap-6 lg:grid-cols-2">
          {PANELS.map((panel) => (
            <article
              key={panel.id}
              aria-labelledby={`panel-${panel.id}`}
              className={cn(
                'flex flex-col gap-6 overflow-hidden rounded-2xl p-6 pb-0 md:p-10 md:pb-0',
                panel.dark ? 'on-navy bg-navy-950 text-white' : 'border border-line-200 bg-navy-50',
              )}
            >
              <div className="flex flex-col gap-3">
                <h3
                  id={`panel-${panel.id}`}
                  className={cn(
                    'font-display text-3xl leading-tight md:text-4xl',
                    panel.dark ? 'text-white' : 'text-navy-900',
                  )}
                >
                  {panel.title}
                </h3>
                <p
                  className={cn(
                    'max-w-md text-base',
                    panel.dark ? 'text-navy-200' : 'text-ink-900',
                  )}
                >
                  {panel.description}
                </p>
              </div>
              <Button asChild variant={panel.dark ? 'inverse' : 'primary'} className="self-start">
                <Link to={panel.to}>
                  {panel.cta}
                  <ArrowRight aria-hidden />
                </Link>
              </Button>
              <figure
                role="img"
                aria-label={panel.previewLabel}
                className="mt-4 flex flex-1 flex-col md:-mr-10"
              >
                {panel.preview}
              </figure>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
